import { Link } from "react-router-dom";
import { Phone, Mail } from "lucide-react";
import SEO, { orgJsonLd, localBusinessJsonLd } from "@/components/SEO";
import Hero from "@/components/Hero";
import EditorialMarquee from "@/components/EditorialMarquee";
import Manifesto from "@/components/Manifesto";
import ServicesGrid from "@/components/ServicesGrid";
import CitiesList from "@/components/CitiesList";
import KeywordAtlas from "@/components/KeywordAtlas";
import { RevealBlock } from "@/components/Reveal";
import { CONTACT } from "@/components/NSLogo";

export default function Home() {
  return (
    <>
      <SEO
        title="Nova Scotia Security Guards & Patrols | NS Security Services"
        description="Discuss guard, mobile patrol and event security for Nova Scotia properties and organizations. Explore service options for Halifax and other communities."
        path="/"
        image="https://images.unsplash.com/photo-1566245024852-04fbf7842ce9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxzZWN1cml0eSUyMGd1YXJkJTIwcGF0cm9sJTIwZm9nfGVufDB8fHx8MTc4NTEwNTY3Mnww&ixlib=rb-4.1.0&q=85"
        keywords={[
          "security guards Halifax",
          "security guards Dartmouth",
          "mobile patrol Halifax",
          "Nova Scotia security company",
          "event security Sydney NS",
        ]}
        jsonLd={[
          orgJsonLd,
          { ...localBusinessJsonLd, telephone: CONTACT.phone, email: CONTACT.email },
        ]}
      />
      <Hero />
      <EditorialMarquee />
      <Manifesto />
      <ServicesGrid compact />
      <CitiesList compact />
      <KeywordAtlas />

      <section className="bg-[#F5F3EF] border-t border-[#D9D6D0] py-20 md:py-28">
        <div
          data-testid="home-directories"
          className="max-w-[1600px] mx-auto px-6 lg:px-12 grid grid-cols-12 gap-8"
        >
          <div className="col-span-12 md:col-span-3">
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#C17A43]">
              — Local references
            </p>
          </div>
          <div className="col-span-12 md:col-span-9">
            <h2 className="font-serif text-2xl md:text-4xl text-[#111111] mb-6 leading-tight">
              Nova Scotia business directories & associations.
            </h2>
            <ul className="border-t border-[#D9D6D0]">
              {[
                "Halifax Chamber of Commerce directory",
                "Cape Breton Regional Chamber listings",
                "Local commercial property association supplier resources",
              ].map((d, i) => (
                <li
                  key={d}
                  className="border-b border-[#D9D6D0] py-5 grid grid-cols-12 gap-4"
                >
                  <span className="col-span-2 font-mono text-[10px] uppercase tracking-[0.28em] text-[#C17A43]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="col-span-10 font-serif text-lg md:text-xl text-[#111111]">
                    {d}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
      <section className="bg-[#111111] text-[#F5F3EF] py-24 md:py-40 border-t border-[#2A2A2A]">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 md:col-span-8">
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#C17A43] mb-6">
              — Reach a supervisor
            </p>
            <RevealBlock>
              <h2 className="font-serif text-[clamp(2.5rem,7vw,7rem)] leading-[0.9] tracking-tighter">
                Phone.<br />
                <span className="italic text-[#C17A43]">Or in writing.</span>
              </h2>
            </RevealBlock>
            <p className="mt-8 font-sans text-base md:text-lg text-[#F5F3EF]/70 max-w-[54ch] leading-relaxed">
              A licensed supervisor answers the phone and every written brief.
              For same-day dispatch across Halifax, Dartmouth, Sydney, Truro,
              and the wider province, call the office directly. For quote
              briefs and documentation, email us — replies within one business day.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <a
                href={CONTACT.phoneHref}
                data-testid="home-cta-phone"
                className="group inline-flex items-center gap-3 bg-[#C17A43] text-[#111111] px-6 py-4 font-mono text-[11px] uppercase tracking-[0.24em] hover:bg-[#F5F3EF] transition-colors duration-300"
              >
                <Phone size={14} strokeWidth={2} />
                {CONTACT.phone}
              </a>
              <a
                href={CONTACT.emailHref}
                data-testid="home-cta-email"
                className="group inline-flex items-center gap-3 border border-[#F5F3EF] text-[#F5F3EF] px-6 py-4 font-mono text-[11px] normal-case tracking-[0.08em] hover:bg-[#F5F3EF] hover:text-[#111111] transition-colors duration-300"
              >
                <Mail size={14} strokeWidth={2} />
                {CONTACT.email}
              </a>
            </div>
          </div>
          <div className="col-span-12 md:col-span-4">
            <Link
              to="/contact"
              className="block border border-[#2A2A2A] p-8 hover:border-[#C17A43] transition-colors"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#C17A43] mb-4">
                — Halifax Office
              </p>
              <p className="font-serif text-xl leading-tight">
                {CONTACT.parentBrand}<br />
                Nova Scotia Security Services<br />
                Barrington Street<br />
                Halifax, Nova Scotia<br />
                B3J · Canada
              </p>
              <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.24em] text-[#F5F3EF]/50">
                Full contact details →
              </p>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
